// src/components/VaccineProtectRequisitionForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faCapsules,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

const VaccineProtectRequisitionForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "PRQ-XXXX-XX-XXX",
    RequestDate: new Date(),
    EstimateYear: "",
    EstimateUnit: "",
    DeliveryTo: "",
    BudgetType: "งบป้องกัน",
    RequestType: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: "",
    RequestDetail: "",
    RequesterID: profile?.Email || "",
    ApprovalStatus: "Pending",
    ApproverID: "",
    ApprovalDate: null,
    Remark: "",
    Status: "Pending",
    VaccineDetails: [],
  });

  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    RequestQuantity: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});

  const unitOptions = [
    "กลุ่มงานโคเนื้อ",
    "กลุ่มงานโคนม",
    "กลุ่มงานสัตว์ปีก",
    "กลุ่มงานสุกร",
  ];

  const budgetTypeOptions = ["Prevention", "Distribution"];

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchRequisition = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineProtectRequisitionsByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const requisition = response.VaccineProtectRequisition;
        const details = response.VaccineDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          const packageSize = vaccine?.PackageSize || 10;
          const requestDoses = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: requestDoses,
          };
        });
        setFormData({
          TransactionID: requisition.TransactionID,
          RequestDate: new Date(requisition.RequestDate),
          EstimateYear: requisition.EstimateYear,
          EstimateUnit: requisition.EstimateUnit,
          DeliveryTo: requisition.DeliveryTo,
          BudgetType: requisition.BudgetType,
          RequestType: requisition.RequestType,
          ReferenceDocumentType: requisition.ReferenceDocumentType,
          ReferenceDocumentNo: requisition.ReferenceDocumentNo,
          WarehouseStaffID: requisition.WarehouseStaffID,
          RequestDetail: requisition.RequestDetail,
          RequesterID: requisition.RequesterID,
          ApprovalStatus: requisition.ApprovalStatus,
          ApproverID: requisition.ApproverID || "",
          ApprovalDate: requisition.ApprovalDate ? new Date(requisition.ApprovalDate) : null,
          Remark: requisition.Remark,
          Status: requisition.Status,
          VaccineDetails: updatedDetails,
        });
      }
    } catch (error) {
      console.error("Error fetching requisition:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchWarehouses();
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchRequisition(transactionId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (date, name) => {
    setFormData({ ...formData, [name]: date });
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    setNewDetail({ ...newDetail, [name]: value });
  };

  const handleQuantityChange = (vaccineId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        const packageSize = vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 10;
        const newDoses = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, RequestDoses: newDoses };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "EstimateYear":
      case "EstimateUnit":
      case "DeliveryTo":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.EstimateYear &&
      formData.EstimateUnit &&
      formData.DeliveryTo &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddDetail = () => {
    if (newDetail.VaccineID && newDetail.RequestQuantity > 0) {
      const selectedVaccine = vaccineOptions.find((v) => v.VaccineID === newDetail.VaccineID);
      if (selectedVaccine) {
        const packageSize = selectedVaccine.PackageSize || 10;
        const newQuantity = parseInt(newDetail.RequestQuantity);
        const newDoses = Math.ceil(newQuantity * packageSize);
        const detailData = {
          VaccineID: newDetail.VaccineID,
          name: selectedVaccine.VaccineNameTH,
          RequestQuantity: newQuantity,
          RequestDoses: newDoses,
        };
        setFormData((prevFormData) => ({
          ...prevFormData,
          VaccineDetails: [...prevFormData.VaccineDetails, detailData],
        }));
        setNewDetail({ VaccineID: "", RequestQuantity: "" });
      } else {
        console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      }
    } else {
      console.warn("Missing required fields:", newDetail);
    }
  };

  const handleRemoveDetail = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID
      ),
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineProtectRequisitions",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, TransactionID: response.TransactionID });
        setIsSubmitted(true);
        alert("สร้างใบเบิกสำเร็จ! เลขที่เอกสาร: " + response.TransactionID);
      }
    } catch (error) {
      console.error("Error creating requisition:", error);
      alert("เกิดข้อผิดพลาดในการสร้างใบเบิก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    const date = formData.RequestDate;
    const formattedRequestDate = date
      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getDate()
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`
      : "";
    const approvalDate = formData.ApprovalDate;
    const formattedApprovalDate = approvalDate
      ? `${(approvalDate.getMonth() + 1).toString().padStart(2, "0")}/${approvalDate
          .getDate()
          .toString()
          .padStart(2, "0")}/${approvalDate.getFullYear()}`
      : null;

    const submitData = {
      ...formData,
      RequestDate: formattedRequestDate,
      ApprovalDate: formattedApprovalDate,
      VaccineDetails: formData.VaccineDetails.map((detail) => ({
        ...detail,
      })),
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineProtectRequisitionsByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบเบิกสำเร็จ!");
        navigate("/vaccineprotectrequisitionlist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditable = !isSubmitted;

  const columns = [
    {
      name: <HStack><FontAwesomeIcon icon={faListOl} /><span>รหัสวัคซีน</span></HStack>,
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faSyringe} /><span>ชื่อวัคซีน</span></HStack>,
      selector: (row) => row.name,
      sortable: true,
      width: "350px",
    },
    {
      name: <HStack><FontAwesomeIcon icon={faCalculator} /><span>จำนวน (ขวด)</span></HStack>,
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.RequestQuantity}
          onChange={(e) => handleQuantityChange(row.VaccineID, e.target.value)}
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
    },
    
        {
          name: <HStack><FontAwesomeIcon icon={faCapsules} /><span>จำนวนโดส</span></HStack>,
          selector: (row) => row.RequestDoses,
          sortable: true,
          cell: (row) => (
            <Box width="100%" ml={10}>
              {row.RequestDoses.toLocaleString()}
            </Box>
          ),
        },
    {
      name: <HStack><FontAwesomeIcon icon={faGears} /><span>การดำเนินการ</span></HStack>,
      cell: (row) => (
        <IconButton.DeleteIcon onClick={() => handleRemoveDetail(row)} size="sm" />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายการเบิกวัคซีนงบป้องกัน
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ข้อมูลใบเบิกวัคซีน
            </Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">เลขที่เอกสาร</FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem> 
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ประเภทงบประมาณ</FormLabel>
              <Input
                name="BudgetType"
                value={formData.BudgetType}
                onChange={handleInputChange}
                colorScheme="iBlue"
                readOnly
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">วันที่ขอเบิก</FormLabel>
              <CustomDatePicker
                value={formData.RequestDate}
                onChange={(date) => handleDateChange(date, "RequestDate")}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.EstimateYear} isDisabled={isSubmitted}>
              <FormLabel fontSize="xl" color="gray">ปีงบประมาณ</FormLabel>
              <Input
                name="EstimateYear"
                value={formData.EstimateYear}
                onChange={handleInputChange}
                placeholder="เช่น 2566"
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.EstimateYear}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.EstimateUnit} isDisabled={isSubmitted}>
              <FormLabel fontSize="xl" color="gray">กลุ่มงาน</FormLabel>
              <Select
                name="EstimateUnit"
                value={formData.EstimateUnit}
                onChange={handleInputChange}
                placeholder="เลือกกลุ่มงาน"
                colorScheme="iBlue"
              >
                {unitOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.EstimateUnit}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">รอบการขอเบิก</FormLabel>
              <Input
                name="RequestType"
                value={formData.RequestType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={2}>
            <FormControl isRequired isInvalid={!!errors.DeliveryTo} isDisabled={isSubmitted}>
              <FormLabel fontSize="xl" color="gray">ส่งมอบไปที่</FormLabel>
              <Select
                name="DeliveryTo"
                value={formData.DeliveryTo}
                onChange={handleInputChange}
                placeholder="เลือกคลังปลายทาง"
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option key={warehouse.WarehouseID} value={warehouse.WarehouseID}>
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.DeliveryTo}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ประเภทเอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={formData.ReferenceDocumentType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">เลขที่เอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={formData.ReferenceDocumentNo}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ผู้ขอเบิก</FormLabel>
              <Input
                name="RequesterID"
                value={formData.RequesterID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
            </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ผู้ประสานงาน</FormLabel>
              <Input
                name="WarehouseStaffID"
                value={formData.WarehouseStaffID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">รายละเอียดการขอเบิก</FormLabel>
              <Textarea
                name="RequestDetail"
                value={formData.RequestDetail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">หมายเหตุ</FormLabel>
              <Textarea
                name="Remark"
                value={formData.Remark}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">สถานะเอกสาร</FormLabel>
              <Input
                name="Status"
                value={formData.Status}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">สถานะการอนุมัติ</FormLabel>
              <Input
                name="ApprovalStatus"
                value={formData.ApprovalStatus}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">ผู้อนุมัติ</FormLabel>
              <Input
                name="ApproverID"
                value={formData.ApproverID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">วันที่อนุมัติ</FormLabel>
              <CustomDatePicker
                value={formData.ApprovalDate}
                onChange={(date) => handleDateChange(date, "ApprovalDate")}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel onClick={() => navigate("/vaccineprotectrequisitionlist")} />
              <IconButton.Submit label="สร้างใบเบิก" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iPurple.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                <Heading size="lg" fontWeight="normal">
                  เพิ่มรายการวัคซีนที่ขอเบิก
                </Heading>
              </HStack>
            </Box>
            <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">วัคซีน</FormLabel>
                <Select
                  name="VaccineID"
                  value={newDetail.VaccineID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iPurple"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">จำนวน (ขวด)</FormLabel>
                <Input
                  name="RequestQuantity"
                  type="number"
                  value={newDetail.RequestQuantity}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <Box mt={10}>
                <IconButton.Add onClick={handleAddDetail} h={9} />
              </Box>
            </Grid>
          </Box>

          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iOrange.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  รายการวัคซีนที่ขอเบิก
                </Heading>
              </HStack>
            </Box>
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </Box>
          </Box>

          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/vaccineprotectrequisitionlist")}
                label="ย้อนกลับ"
              />
              <IconButton.Save onClick={handleConfirm} isLoading={isLoading} />
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VaccineProtectRequisitionForm;